import { LinksViewOptions, MultiContextData, MultiContextDataItem, ReportContext, TextViewOptions } from "@reporting/rp-types/analytics";
import { DashboardItemModel, DashboardItemSource as OGDashboardItemSource, DashboardModel, DashboardOptions as OGDashboardOptions } from "@omegagrid/dashboard";
import { ComponentAdapter, ComponentId, MenuItem } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { openContextFilter, openReport } from "../../actions";
import rp, { parseComponentId } from "@reporting/rp-app-host";
import { Color } from "@omegagrid/grid";
import constants from "../../constants";
import { UidMultiContextDataItem, createDashboardItemComponentId, isItemUid } from "../../common";
import { ReportStore } from "../../model/reportStore";
import type { AnalyticsApplication } from "../..";

const app = () => rp.applications.get('analytics') as AnalyticsApplication;

export type DashboardOptions = OGDashboardOptions & {
	contextMenuButton?: boolean;
	/** TODO: remove once @omegagrid/dashboard with overlay.renderer support is released */
	overlay?: OGDashboardOptions['overlay'] & {
		renderer?: (div: HTMLDivElement, model: DashboardItemModel) => void;
	};
}

export type DashboardItemStyle = {
	header?: boolean;
	/** no chrome (border, background, shadow, header), only the item content */
	transparent?: boolean;
	color?: Color;
	fgColor?: Color;
	border?: {
		width?: number;
		color?: Color;
		style?: 'solid' | 'dashed' | 'dotted' | 'double';
		radius?: number;
	}
}

export type DashboardItemSource = OGDashboardItemSource & {
	index?: number;
	menu?: MenuItem[];
	style?: DashboardItemStyle;
}

export abstract class DashboardAdapter extends ComponentAdapter {

	abstract readonly reportContext: ReportContext;

	/** backing store of the dashboard context; null for storeless (derived) dashboards */
	get store(): ReportStore {
		return null;
	}

	get contextData(): MultiContextData {
		return this.reportContext?.context as MultiContextData;
	}

	abstract getItems(): Promise<DashboardItemSource[]>|DashboardItemSource[];

	abstract getOptions(): Promise<DashboardOptions>|DashboardOptions;

	abstract updateContext(model: DashboardModel): MultiContextData;

	protected createOptions(options: Partial<DashboardOptions>): DashboardOptions {
		options = Object.assign({}, options);
		return options as DashboardOptions;
	}

	onAction(action: string, item?: DashboardItemSource) { }

	removeItem(id: ComponentId) { }

	duplicateItem(id: ComponentId) { }

	/** apply a recipe to the item's context data, returns the updated item source */
	updateItem(id: ComponentId, recipe: (item: UidMultiContextDataItem) => void) : DashboardItemSource {
		return null;
	}

	updateOptions(options: OGDashboardOptions) { }

	split(dashboardItemModel: DashboardItemModel, count: number) { }

}


/**
 * Adapter of a stored dashboard context. Reads come from the store draft
 * (a fresh immutable snapshot after every change), writes go through the
 * store mutation API - the adapter itself holds no state.
 */
export class MultiContextDashboardAdapter extends DashboardAdapter {

	constructor(private readonly _store: ReportStore) { super() }

	get store(): ReportStore {
		return this._store;
	}

	get reportContext(): ReportContext {
		return this._store.context;
	}

	get contextData(): MultiContextData {
		return this._store.contextData as MultiContextData;
	}

	get dashboardRowCount() {
		return Math.round(2 * this.getOptions().columns / 3);
	}

	/** uid carried in the component id */
	protected itemUidOf(id: ComponentId): string {
		return parseComponentId(id).ids[2] as string;
	}

	getItems(): DashboardItemSource[] {
		return (this.contextData.items ?? []).map((item: UidMultiContextDataItem) => ({
			id: createDashboardItemComponentId(this._store.idContext, item),
			title: item.title || ' ',
			x: item.x || 0,
			y: item.y || 0,
			w: item.width || 8,
			h: item.height || 4,
			style: item.style,
			hidden: this.isItemHidden(item),
			menu: [
				// only a context connected to a module can have filters
				...(item.context?.module ? [{k: 'filter', v: msg('Filter'), icon: 'filter'}] : []),
				// a linked view or an embedded report (not a text/links item without a module)
				...(item.id_context || item.context?.module ? [{k: 'standalone', v: msg('Open standalone'), icon: 'arrow-up-right-from-square'}] : []),
			]
		}));
	}

	/**
	 * Items the user has no use for are hidden outside design mode (view_options.validate_access):
	 * a text linking a view the user cannot open and a links item without any link the user can
	 * open. The server marks them with access=false, they stay in the context so saving does not
	 * drop them for the other users.
	 */
	isItemHidden(item: MultiContextDataItem) {
		if (this.contextData.view_options?.validate_access === false) return false;
		const viewOptions = item.context?.view_options as TextViewOptions & LinksViewOptions;
		switch (item.context?.view) {
			case 'text': return !!viewOptions?.id_context && viewOptions.access === false;
			case 'links': return !!viewOptions?.items?.length && viewOptions.items.every(link => link.access === false);
		}
		return false;
	}

	/**
	 * Open the item as a report of its own in a new tab. A linked item opens
	 * its view; an embedded definition becomes a new stored context built
	 * from the item as displayed (the common dashboard filters merged in).
	 */
	async openStandalone(item: DashboardItemSource) {
		const index = this._store.indexOf(this.itemUidOf(item.id));
		const dataItem = this.contextData.items?.[index];
		if (!dataItem) return;

		if (dataItem.id_context) {
			await openReport(dataItem.id_context, {target: 'new_tab', title: dataItem.title});
			return;
		}

		const subcontext = await app().contextBroker.getSubcontext(this.reportContext, index);
		if (!subcontext?.context) return;
		const context = await app().contextBroker.getContext({module: subcontext.module, context: subcontext.context});
		await openReport(context.id_context, {context, target: 'new_tab', title: dataItem.title || context.name});
	}

	updateContext(model: DashboardModel) {
		this._store.updateItemGeometry(model.items
			.filter(item => isItemUid(this.itemUidOf(item.id)))
			.map(item => ({
				uid: this.itemUidOf(item.id),
				x: item.x,
				y: item.y,
				width: item.w,
				height: item.h,
			}))
		);
		return this.contextData;
	}

	getOptions(): DashboardOptions {
		return {
			columns: constants.DASHBOARD_COLUMNS,
			gap: 10,
			orientation: 'vertical',
			// items hidden by the access validation (isItemHidden) leave no gaps
			autoCompact: true,
			...this.contextData.view_options
		};
	}

	split(dashboardItemModel: DashboardItemModel, count: number) {
		this._store.splitItem(this.itemUidOf(dashboardItemModel.id), count);
	}

	onAction(action: string, item?: DashboardItemSource) {
		switch (action) {
			case 'filter':
				openContextFilter(this._store.idContext, item?.index);
				break;
			case 'standalone':
				this.openStandalone(item);
				break;
		}
	}

	updateOptions(options: OGDashboardOptions) {
		this._store.updateViewOptions(viewOptions => Object.assign(viewOptions, options));
	}

	updateItem(id: ComponentId, recipe: (item: UidMultiContextDataItem) => void) {
		const uid = this.itemUidOf(id);
		if (!this._store.updateItem(uid, recipe)) return null;
		return this.getItems()[this._store.indexOf(uid)] ?? null;
	}

	removeItem(id: ComponentId) {
		this._store.removeItem(this.itemUidOf(id));
	}

	duplicateItem(id: ComponentId) {
		this._store.duplicateItem(this.itemUidOf(id));
	}

}

const COLUMNS = 4;

// const DASHBOARD_ROWS = Math.round(2 * DASHBOARD_COLUMNS / 3);

export class SingleContextDashboardAdapter extends DashboardAdapter {

	constructor(
		readonly reportContext: ReportContext,
		readonly items: MultiContextDataItem[]
	) { super() }

	get dashboardRowCount() {
		return Math.round(2 * constants.DASHBOARD_COLUMNS / 3);
	}

	getItems(): DashboardItemSource[] {
		const dashboardRowCount = this.dashboardRowCount;

		const w = Math.floor(this.items.length < COLUMNS 
			? (constants.DASHBOARD_COLUMNS / this.items.length)
			: (constants.DASHBOARD_COLUMNS / COLUMNS)
		);
		const rowCount = Math.ceil(this.items.length / COLUMNS);
		const h = Math.round(dashboardRowCount / rowCount);
		// const h = rowCount

		return this.items.map((item, i) => ({
			id: i,
			title: item.title,
			x: i % COLUMNS * w,
			y: Math.floor(i / COLUMNS) * h,
			w: w,
			h: h,
		}));
	}

	updateContext(model: DashboardModel) {
		return this.reportContext.context as MultiContextData;
	}

	getOptions(): DashboardOptions {
		return {
			columns: constants.DASHBOARD_COLUMNS,
			rows: this.dashboardRowCount,
			gap: constants.DASHBOARD_ITEM_GAP,
			orientation: 'vertical'
		};
	}

}
